import { gsap } from 'gsap';
import type { ThreeStage } from '../../../three/ThreeStage';
import { BaseScene } from '../../../core/BaseScene';
import { glyphGroup, type GlyphId } from '../../../components/glyphs';
import { seeded } from '../../../components/random';
import { draw, pulse } from '../../../components/svg';
import { words, revealWords, settleWords } from '../../../components/text';

const VERBS = ['Understand', 'Design', 'Build', 'Connect', 'Learn', 'Evolve'];
const CELL = 60;
const GAP = 12;
const COLS = 4;
const ROWS = 4;
const X0 = 800 - (COLS * CELL + (COLS - 1) * GAP) / 2;
const Y0 = 500 - (ROWS * CELL + (ROWS - 1) * GAP) / 2;
const cellX = (c: number): number => X0 + c * (CELL + GAP);
const cellY = (r: number): number => Y0 + r * (CELL + GAP);
const GRID_W = COLS * CELL + (COLS - 1) * GAP;
const GRID_H = ROWS * CELL + (ROWS - 1) * GAP;

/** Scattered inputs, then a loose cluster: the before and after of understanding. */
const random = seeded(1318);
const SCATTER = Array.from({ length: COLS * ROWS }, () => ({ x: 180 + random() * 1180, y: 220 + random() * 520 }));
const CLUSTER = Array.from({ length: COLS * ROWS }, () => ({ x: 770 + (random() - 0.5) * 220, y: 470 + (random() - 0.5) * 180 }));

const EXTERNAL: ReadonlyArray<[GlyphId, string, number, number]> = [
  ['api', 'APIs', 430, 380],
  ['database', 'Data', 430, 620],
  ['customer', 'Users', 1170, 380],
  ['cloud', 'Services', 1170, 620],
];
const FEEDBACK = `M1170 352 C1170 200 800 190 800 ${Y0 - 6}`;

/**
 * 0 UNDERSTAND — scattered fragments gather
 * 1 DESIGN     — they snap into a structure
 * 2 BUILD      — the structure becomes solid
 * 3 CONNECT    — it links to the systems around it
 * 4 LEARN      — feedback flows back in
 * 5 EVOLVE     — it grows
 * 6 SOFTWARE IS NEVER THE FIRST STEP.
 * 7 UNDERSTANDING IS.
 */
export class ProcessScene extends BaseScene {
  public readonly stepCount = 8;

  private learning = false;

  protected template(): string {
    const cells = Array.from({ length: COLS * ROWS }, (_, i) =>
      `<rect class="pr-cell" data-cell="${i}" width="${CELL}" height="${CELL}" rx="10"/>`).join('');
    const grown = Array.from({ length: ROWS }, (_, r) =>
      `<rect class="pr-cell pr-cell--new" data-from="5" x="${cellX(COLS)}" y="${cellY(r)}" width="${CELL}" height="${CELL}" rx="10"/>`).join('');

    const construction = [
      `M${X0 - 60} ${Y0 - 20} H${X0 + GRID_W + 60}`,
      `M${X0 - 60} ${Y0 + GRID_H + 20} H${X0 + GRID_W + 60}`,
      `M${X0 - 20} ${Y0 - 60} V${Y0 + GRID_H + 60}`,
      `M${X0 + GRID_W + 20} ${Y0 - 60} V${Y0 + GRID_H + 60}`,
    ].map((d) => `<path class="pr-guide draw" pathLength="1" d="${d}"/>`).join('');

    const external = EXTERNAL.map(([id, label, x, y]) => {
      const left = x < 800;
      const edge = left ? X0 - 4 : X0 + GRID_W + 4;
      return `<g class="pr-ext" data-from="3">
        <path class="pr-ext__link draw" pathLength="1" d="M${left ? x + 30 : x - 30} ${y} C${(x + edge) / 2} ${y} ${(x + edge) / 2} ${500 + (y - 500) * 0.4} ${edge} ${500 + (y - 500) * 0.4}"/>
        <g transform="translate(${x} ${y})">
          <circle class="pr-ext__disc" r="28"/>
          ${glyphGroup(id, 24)}
          <text class="pr-ext__label" y="52" text-anchor="middle">${label}</text>
        </g>
      </g>`;
    }).join('');

    const verbs = VERBS.map((verb, i) => `
      <g class="pr-verb" data-verb="${i}">
        ${i > 0 ? `<path class="pr-verb__link" d="M${330 + (i - 1) * 188 + 70} 110 H${330 + i * 188 - 70}"/>` : ''}
        <text x="${330 + i * 188}" y="118" text-anchor="middle">${verb.toUpperCase()}</text>
      </g>`).join('');

    return `
      <div class="scene-layout process">
        <svg class="diagram pr-diagram" viewBox="0 0 1600 900" role="img" aria-label="Understand, design, build, connect, learn, evolve">
          <g class="pr-verbs">${verbs}</g>
          <g class="pr-construction">${construction}</g>
          ${external}
          <path class="pr-feedback draw" data-from="4" pathLength="1" d="${FEEDBACK}"/>
          <g class="pr-grid">${cells}${grown}</g>
          <g class="pr-pulses">${[0, 1, 2].map(() => '<circle class="pr-pulse" r="5"/>').join('')}</g>
        </svg>
        <div class="statement-stack pr-statements">
          <h2 class="statement" data-from="6">${words('SOFTWARE IS NEVER THE FIRST STEP.')}</h2>
          <h2 class="statement statement--accent" data-from="7">${words('UNDERSTANDING IS.')}</h2>
        </div>
      </div>`;
  }

  protected onEnter(immediate: boolean): void {
    if (immediate || this.step !== 0) return;
    // Fragments arrive from everywhere before they gather.
    const tl = this.timeline();
    this.$$<SVGRectElement>('.pr-cell:not(.pr-cell--new)').forEach((cell, i) => {
      const from = SCATTER[i];
      const to = CLUSTER[i];
      tl.fromTo(cell, { attr: { x: from.x, y: from.y }, opacity: 0 }, { attr: { x: to.x, y: to.y }, opacity: 1, duration: 2.2, ease: 'power3.inOut' }, 0.1 + i * 0.05);
    });
  }

  protected onExit(): void {
    this.learning = false;
  }

  protected onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void {
    stage.setAccent(this.meta.accent);
    stage.setMode('process', step, immediate);

    const forward = !immediate && step > previousStep;
    const d = immediate ? 0 : 1;
    const tl = this.timeline();

    this.$$('.pr-verb').forEach((verb, i) => {
      verb.dataset.state = i === Math.min(step, 5) && step <= 5 ? 'active' : i <= step ? 'done' : '';
    });

    // Cells: a loose cluster until design gives them their place in the structure.
    this.$$<SVGRectElement>('.pr-cell:not(.pr-cell--new)').forEach((cell, i) => {
      const placed = step >= 1;
      const x = placed ? cellX(i % COLS) : CLUSTER[i].x;
      const y = placed ? cellY(Math.floor(i / COLS)) : CLUSTER[i].y;
      gsap.to(cell, {
        attr: { x, y },
        opacity: 1,
        duration: d * 1.1,
        delay: forward && step === 1 ? (i % COLS) * 0.05 + Math.floor(i / COLS) * 0.05 : 0,
        ease: 'expo.inOut',
        overwrite: 'auto',
      });
    });

    const grid = this.$('.pr-grid');
    grid.dataset.state = step >= 2 ? 'built' : step === 1 ? 'designed' : 'raw';
    const guides = this.$$('.pr-guide');
    if (forward && step === 1) tl.add(draw(guides, true, { duration: 1, stagger: 0.1 }), 0.3);
    else draw(guides, step >= 1, { immediate: true });
    gsap.to(this.$('.pr-construction'), { opacity: step >= 2 ? 0.35 : 1, duration: d * 0.6 });

    const cells = this.$$('.pr-cell:not(.pr-cell--new)');
    if (forward && step === 2) tl.fromTo(cells, { '--fill': 0 }, { '--fill': 1, duration: 0.6, stagger: { each: 0.04, from: 'start', grid: [ROWS, COLS] } }, 0.1);
    else gsap.set(cells, { '--fill': step >= 2 ? 1 : 0 });

    const links = this.$$('.pr-ext__link');
    if (forward && step === 3) {
      draw(links, false, { immediate: true });
      tl.add(draw(links, true, { duration: 0.9, stagger: 0.15, ease: 'power2.out' }), 0.4);
    } else {
      draw(links, step >= 3, { immediate: true });
    }

    const feedback = this.$('.pr-feedback');
    if (forward && step === 4) tl.add(draw(feedback, true, { duration: 1.2, ease: 'power2.inOut' }), 0.2);
    else draw(feedback, step >= 4, { immediate: true });
    this.syncLearning(step >= 4 && step <= 5, forward && step === 4);

    // Evolve: new capacity grows and the structure re-centres around it.
    const grownCells = this.$$('.pr-cell--new');
    if (forward && step === 5) {
      tl.fromTo(grownCells, { scale: 0, transformOrigin: '50% 50%' }, { scale: 1, duration: 0.7, stagger: 0.12, ease: 'back.out(2)' }, 0.2);
    } else {
      gsap.set(grownCells, { scale: 1, transformOrigin: '50% 50%' });
    }
    gsap.to(grid, { x: step >= 5 ? -(CELL + GAP) / 2 : 0, duration: d * 1, ease: 'power3.inOut' });
    grid.classList.toggle('is-evolved', step >= 5);

    // The statements take over; the object steps back.
    gsap.to(this.$('.pr-diagram'), { opacity: step >= 6 ? 0.14 : 1, duration: d * 0.8 });
    this.$$('.pr-statements .statement').forEach((s) => settleWords(s));
    if (forward && step >= 6) tl.add(revealWords(this.$$('.pr-statements .statement')[step - 6]), 0.2);
  }

  private syncLearning(on: boolean, delayed: boolean): void {
    if (on === this.learning) return;
    this.learning = on;
    if (!on) {
      this.stopLoops();
      this.$$('.pr-pulse').forEach((m) => { m.style.opacity = '0'; });
      return;
    }
    const path = this.$<SVGPathElement>('.pr-feedback');
    this.$$<SVGCircleElement>('.pr-pulse').forEach((marker, i) => {
      this.loop(pulse(marker, path, { duration: 1.8, delay: (delayed ? 1.3 : 0) + i * 0.7, repeatDelay: 0.3 }));
    });
  }
}
